import { Tooltip } from 'antd';
import classNames from 'classnames';
import { upperCase } from 'lodash';
import { useTranslation } from 'react-i18next';

import { IMAGE_CUSTOMER_COOKTOP, IMAGE_CUSTOMER_RANGE, TOOLTIP_PLACEMENT, UNIT_MEASURE } from '@common/constants';
import cssColors from '@common/css/cssColors.module.scss';
import { CookingApplianceOptions } from '@common/enums';
import formatUOM from '@common/helpers/formatUOM';
import { getOnTrendLabelFromProduct } from '@common/helpers/onTrendLabel';
import { ApplianceModel, ApplianceType } from '@common/models';
import { getProductDescription } from '@common/utils';
import { formatPriceUnitOrFree } from '@common/utils/formatPriceUnitOrFree';
import ApplianceDragableImage from '@core/components/ApplianceDragableImage/ApplianceDragableImage';
import InstructionsBanner from '@core/components/instructions-banner/InstructionsBanner';
import ImageUtils from '@core/utils/ImageUtils';
import { useSelectCutoutLabor } from '@ee-api/cutout-labor-api/hooks/useCutoutLaborProducts';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    selectSelectedCookingApplianceId,
    selectSelectedCookingApplianceOption,
    updateSelectedCookingApplianceId,
} from '@redux/slices/cookingApplianceSlice';

export interface CookingApplianceItemProps {
    cookingAppliance: ApplianceModel;
}

const CookingApplianceItem = ({ cookingAppliance }: CookingApplianceItemProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { cooktopCutout } = useSelectCutoutLabor();
    const selectedCookingApplianceId = useAppSelector(selectSelectedCookingApplianceId);
    const selectedCookingApplianceOption = useAppSelector(selectSelectedCookingApplianceOption);
    const description = getProductDescription(cookingAppliance);

    const onTrendLabel = getOnTrendLabelFromProduct(cookingAppliance);

    const isCooktop =
        upperCase(cookingAppliance.type) === ApplianceType.COOKTOP || // for real appliances
        upperCase(cookingAppliance.consumerDesc) === ApplianceType.COOKTOP; // for customer supplied

    const isSelected = cookingAppliance.productId === selectedCookingApplianceId;

    const applianceDragSrc = isCooktop ? IMAGE_CUSTOMER_COOKTOP : IMAGE_CUSTOMER_RANGE;

    // card is selected, it's a COOKTOP, cooktopCutout has been found for it, and the user hasn't
    // selected "No Cooking Appliances" button
    const showCutout =
        isCooktop &&
        isSelected &&
        cooktopCutout &&
        selectedCookingApplianceOption &&
        selectedCookingApplianceOption !== CookingApplianceOptions.NONE;

    const unitOfMeasure = formatUOM(cookingAppliance.unitOfMeasure || UNIT_MEASURE.COOKING_APPLIANCE);

    const clonedCookingAppliance = {
        ...cookingAppliance,
        applianceType:
            upperCase(cookingAppliance.type) === ApplianceType.COOKTOP ||
            upperCase(cookingAppliance.consumerDesc) === ApplianceType.COOKTOP // for customer supplied
                ? ApplianceType.COOKTOP
                : ApplianceType.RANGE,
    };

    return (
        <div
            className={classNames('card-option', {
                selected: isSelected,
            })}
            onMouseUp={() => {
                dispatch(updateSelectedCookingApplianceId(cookingAppliance.productId));
            }}
        >
            {onTrendLabel && <div className="on-trend">{onTrendLabel}</div>}
            <ApplianceDragableImage
                alt="cooking-appliance"
                className="card-option__image"
                src={ImageUtils.imgUrl(cookingAppliance.beautyShotImageUrl || cookingAppliance.imageUrl, 300)}
                applianceModel={clonedCookingAppliance}
                applianceDragSrc={applianceDragSrc}
            />
            <InstructionsBanner className="card-option__banner" hidden={!isSelected} />
            <Tooltip title={description} color={cssColors.toolTipColor} placement={TOOLTIP_PLACEMENT}>
                <div className="name">{description}</div>
            </Tooltip>
            {selectedCookingApplianceOption && selectedCookingApplianceOption !== CookingApplianceOptions.SUPPLY && (
                <div className="price">{formatPriceUnitOrFree(cookingAppliance.price, unitOfMeasure)}</div>
            )}
            {showCutout && (
                <div className="card-option__cutout-charge">
                    {t('SCROLLABLE_AREA.COOKINGAPPLIANCE.CUTOUT_CHARGE_INFO', {
                        name:
                            getProductDescription(cooktopCutout, true) ||
                            t('SCROLLABLE_AREA.COOKINGAPPLIANCE.CUTOUT_CHARGE_GENERIC_NAME'),
                        price: formatPriceUnitOrFree(cooktopCutout.price),
                    })}
                </div>
            )}
        </div>
    );
};

export default CookingApplianceItem;
